"use client";

import { GetApiAuthDiscoverAuthUserResponses } from "@nimble/core-client/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { create } from "zustand";

import { getUseApiUserDiscoverInfoQueryKey } from "@/hooks/data/use-api-user-discover-info";
import coreApiClient from "@/lib/api-clients/core-api-client";
import { type User, userSchema } from "@/lib/types/auth";
import { usePreserveFilters } from "@/stores/use-preserve-filters";

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  hasCheckedAuthStatus: boolean;
}

export interface AuthActions {
  loadAuth: (
    fetchAuthDataFn?: (getApiAuth: GetApiAuthFn) => Promise<AuthState>,
  ) => Promise<void>;
  clearAuth: () => void;
  syncAuth: (
    fetchAuthDataFn?: (getApiAuth: GetApiAuthFn) => Promise<AuthState>,
  ) => void;
}

export type AuthStore = AuthState & AuthActions;

const defaultInitState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hasCheckedAuthStatus: false,
};

// Module-level flag to prevent concurrent initialization attempts and duplicate API calls
// This is checked and set synchronously before any async operations
let isInitializing = false;

type SuccessAuthType<T> = Extract<T, { id: number }>;

const baseData: Omit<AuthState, "user"> = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
  hasCheckedAuthStatus: true,
};

export type GetApiAuthFn = typeof coreApiClient.getApiAuthDiscoverAuthUser;

export function parseAuthData(
  data: GetApiAuthDiscoverAuthUserResponses["200"],
): AuthState {
  if (data?.id) {
    const successData = data as SuccessAuthType<typeof data>;
    const user = userSchema.decode(successData);

    return {
      ...baseData,
      user,
      isAuthenticated: !!user,
    };
  }

  return {
    ...baseData,
    user: null,
  };
}

export async function fetchAuthData(
  getApiAuth: GetApiAuthFn,
): Promise<AuthState> {
  const { data } = await getApiAuth();
  return parseAuthData(data);
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function createErrorState(error: unknown): AuthState {
  return {
    ...baseData,
    user: null,
    error: toError(error),
  };
}

const useAuthStore = create<AuthStore>()((set) => ({
  ...defaultInitState,

  // Silently sync the auth data, without triggering any loading states
  syncAuth: async (fetchAuthDataFn = fetchAuthData) => {
    try {
      const data = await fetchAuthDataFn(
        coreApiClient.getApiAuthDiscoverAuthUser,
      );
      set(data);
    } catch (error) {
      set(createErrorState(error));
      console.error(error);
    }
  },

  loadAuth: async (fetchAuthDataFn = fetchAuthData) => {
    if (isInitializing) return;

    isInitializing = true;
    set({ isLoading: true });

    try {
      const data = await fetchAuthDataFn(
        coreApiClient.getApiAuthDiscoverAuthUser,
      );
      set(data);
    } catch (error) {
      set(createErrorState(error));
      console.error(error);
    } finally {
      isInitializing = false;
    }
  },

  clearAuth: () => {
    set({ ...defaultInitState, isLoading: false, hasCheckedAuthStatus: true });
    isInitializing = false;
  },
}));

export const useSignOut = () => {
  const authStore = useAuthStore();
  const { clearAuth, loadAuth } = authStore;
  const queryClient = useQueryClient();
  const { clearPreservedFilters } = usePreserveFilters();

  const signOut = useCallback(async () => {
    await coreApiClient.deleteApiAuth();
    queryClient.removeQueries({
      queryKey: getUseApiUserDiscoverInfoQueryKey(),
      exact: false,
    });
    clearPreservedFilters();
    clearAuth();
    await loadAuth();
  }, [clearAuth, loadAuth, queryClient, clearPreservedFilters]);

  return { signOut };
};

export const useAuth = (fetchAuthDataFn = fetchAuthData) => {
  const authStore = useAuthStore();
  const { hasCheckedAuthStatus, loadAuth } = authStore;

  useEffect(() => {
    if (!hasCheckedAuthStatus) {
      loadAuth(fetchAuthDataFn);
    }
  }, [hasCheckedAuthStatus, loadAuth, fetchAuthDataFn]);

  return authStore;
};
